import { ReactElement, ReactNode } from 'react'
import { GoogleTagManager } from '@next/third-parties/google'
import { NextPage } from 'next'
import { type AppContext, type AppProps, default as NextApp } from 'next/app'

import SVGSprites from '@/components/base/SVGSprites'
import { Store, StoreProvider } from '@/contexts/store'
import { ThemeProvider } from '@/contexts/theme'
import { TransitionProvider } from '@/contexts/transition'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import '@/styles/globals.css'

import '@/base/gsap'

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line no-unused-vars
  getLayout?: (page: ReactElement, props: AppProps) => ReactNode
}

export default function App({
  Component,
  pageProps,
  menu,
  strings
}: AppProps & Store & { Component: NextPageWithLayout }) {
  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <>
      <StoreProvider menu={menu} strings={strings}>
        <ThemeProvider theme={pageProps?.acf?.theme}>
          <TransitionProvider>
            {getLayout(<Component {...pageProps} />, pageProps)}
          </TransitionProvider>
          <SVGSprites />
        </ThemeProvider>
      </StoreProvider>
      {process.env.NEXT_PUBLIC_GTM_ID ? (
        <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_ID} />
      ) : null}
    </>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)
  const lang = appContext.router.locale

  const { data: menuData } = await getPromiseFromFetch({
    method: 'get',
    url: `${API.MENU_LOCATIONS}?lang=${lang}`
  })

  const { data: optionsData } = await getPromiseFromFetch({
    method: 'get',
    url: `${API.STRINGS}?lang=${lang}`
  })

  return {
    ...appProps,
    strings: optionsData,
    menu: menuData
  }
}
